import React from 'react';
import { parseLocalDate, formatDateToLocal } from '../../lib/dateUtils';
import { calculateDueDate as calcDueDateUtil, calculateDueDateByGap } from '../../lib/sopRules';

/**
 * DragConfirmDialog
 * ─────────────────────────────────────────────────────────────────────────
 * Surfaces when a user drags a task to a new date on the calendar grid.
 * Confirms the move and previews:
 *   • Publish (use_date) transition
 *   • Internal due_date transition (respects task.due_date_sop buffer if set,
 *     else preserves the existing use→due gap)
 *
 * Props:
 *   pendingDragUpdate : { task, newDate }    — null when no drag in flight
 *   holidays          : array                — SOP buffer respects holidays
 *   onCancel          : () => void
 *   onConfirm         : () => void
 *
 * Pure-presentation: no internal state, no side effects.
 * Renders nothing when pendingDragUpdate is null.
 */
export default function DragConfirmDialog({ pendingDragUpdate, holidays, onCancel, onConfirm }) {
  if (!pendingDragUpdate) return null;

  const { task, newDate } = pendingDragUpdate;
  const newUseObj = parseLocalDate(newDate);

  let newDueObj;
  if (task.due_date_sop) {
    newDueObj = calcDueDateUtil(newUseObj, task.due_date_sop, holidays);
  } else if (task.use_date && task.due_date) {
    newDueObj = calculateDueDateByGap(newUseObj, task.use_date, task.due_date);
  } else {
    newDueObj = newUseObj;
  }
  const newDueStr = formatDateToLocal(newDueObj);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="drag-confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="drag-confirm-title"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 id="drag-confirm-title" className="text-lg font-bold text-[#1a1a2e] mb-4">
          Confirm Date Change
        </h3>
        <p className="text-neutral-600 mb-2">Are you sure you want to move this task?</p>
        <div className="bg-white/40 rounded-xl p-4 mb-4 space-y-2">
          <p className="font-medium text-[#1a1a2e]">{task.title}</p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-500 w-24">Publish (use):</span>
            <span className="font-medium text-neutral-700">
              {task.use_date || task.due_date}
            </span>
            <span className="text-neutral-400">→</span>
            <span className="font-medium text-pink-600">{newDate}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-500 w-24">Internal due:</span>
            <span className="font-medium text-neutral-700">{task.due_date || '—'}</span>
            <span className="text-neutral-400">→</span>
            <span className="font-medium text-emerald-600">{newDueStr}</span>
            {task.due_date_sop && (
              <span className="text-xs text-neutral-400 ml-1">
                ({task.due_date_sop.replace(/_/g, ' ')})
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-neutral-700 border border-white/40 rounded-xl hover:bg-white/60 transition-colors"
            data-testid="drag-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-colors"
            data-testid="drag-confirm-save"
          >
            Update Date
          </button>
        </div>
      </div>
    </div>
  );
}
